/**
 * shareDoctorPanel.js — the two sections of the share-code screen's doctor panel
 * (v242.207.0). The panel opens from DiagnoseScreen's "Cancel share code → Share
 * with an Orella Doctor instead" link.
 *
 * Alex's rulings, 2026-09-23:
 *   - "Your Doctors" is MyDoctorsScreen's set: favourites plus previously-seen
 *     doctors. It contains Orella doctors only, active and accepting only, and it
 *     is NOT filtered by the case's specialty. The case's external doctor (a
 *     Google Places record with no `doctors.id`) is never in it.
 *   - When "Your Doctors" is empty, the section is hidden.
 *   - "Orella doctors" is the picker's own list: the case-matched doctors
 *     (matchDoctorsToCase), with anyone already in "Your Doctors" left out, so
 *     that one doctor is never offered twice.
 *
 * ★ Every doctor here comes from the patient-visible directory (GET
 *   /doctors/available). That endpoint already filters on `status='active'` and
 *   `accepting_patients` not false. The same predicate is applied again here, so a
 *   seen doctor who has since been deactivated cannot enter the list through a
 *   by-id lookup. MyDoctorsScreen does that lookup, and the panel must not.
 *
 * ★ D-g (2026-09-25): both sections also required real hours (hasRealHours below).
 * ★★ D527 (Dr. Rami 2026-10-23, time request; handoff s116) REVERSES that here: an active physician who accepts
 *   patients is selectable with or without hours; without hours the patient REQUESTS a time and the physician
 *   confirms it (utils/bookableSlots requestGridSlots). hasRealHours stays: the Doctors tab badge reads it.
 *
 * Pure and free of imports, so scripts/check-share-link-doctor-panel.mjs can
 * EXECUTE it with synthetic rows instead of pattern-matching its text.
 */

/**
 * hasRealHours — D-g (Alex, 2026-09-25). Sending a case to an Orella doctor REQUIRES a
 * time, so a physician without real hours is not selectable at all in the share flow:
 * not in "Your Doctors", not in "Orella doctors", not in ShareScreen's matched list.
 *
 * ★★ ONE OWNER, ON THE SERVER. GET /doctors/available carries `bookable`, which is
 *   _doctor_availability_usable(doctor) (scheduling ON and at least one weekday ON).
 *   When it is a boolean it is the answer, and the client does not second-guess it.
 * ★★ WHEN `bookable` IS ABSENT (an API from before the P1 release). Nothing measured in
 *   this repo proves the deployed API sends it on every row, and the app and the API
 *   ship independently, so a missing key is NOT read as "not bookable" (that would
 *   hide every physician until the API release) and NOT as "bookable" (that would put
 *   a no-hours physician back in the list). It falls back to the evidence the same
 *   endpoint has sent since v195.2: `next_available_date`, which the API sets only when
 *   availability_enabled is on AND an enabled weekday with valid hours falls in the next
 *   30 days that are not blocked. That is a subset of "usable", so the fallback can hide
 *   a physician whose next 30 days are all blocked, and it never shows one without hours.
 * ★★ WHERE IT APPLIES (P1 round 4, L2): only where a pick leads to a send. The share
 *   panel and ShareScreen's matched list, the directory's "Orella doctors" list (rows
 *   from /doctors/available), DoctorProfileScreen's "Start a case" / "Select Doctor"
 *   CTA and ShareScreen's 'orella-preferred-doctor' handoff. GET /doctors/<id> and
 *   /doctors/<id>/full-profile carry the same `bookable` (orella-api round 4, K1).
 *   My Doctors is a directory, not a picker, and hides nobody with it.
 */
export function hasRealHours(d) {
  if (!d || typeof d !== 'object') return false
  if (typeof d.bookable === 'boolean') return d.bookable
  return typeof d.next_available_date === 'string' && d.next_available_date.length > 0
}

const isBookable = (d) => !!d && !!d.id && d.status === 'active' && d.accepting_patients !== false   // D527: hours no longer required

const bySoonest = (a, b) => {
  const da = a.next_available_date || 'z'
  const db = b.next_available_date || 'z'
  return da.localeCompare(db)
}

// DoctorCard reads `_nextSlot`; the directory row carries a date only, the same
// shape ShareScreen gives it.
const withNextSlot = (d) => ({
  ...d,
  _nextSlot: d._nextSlot !== undefined ? d._nextSlot : (d.next_available_date ? { date: d.next_available_date } : null),
})

/**
 * @param {object}   args
 * @param {object[]} args.available    rows from doctorsAPI.list()  (GET /doctors/available)
 * @param {string[]} args.favoriteIds  from favoritesAPI.getDoctors()
 * @param {object[]} args.cases        the patient's cases (casesAPI.list()); `doctor_id` marks a seen doctor
 * @param {object[]} args.matched      matchDoctorsToCase(available, matchingSpecialties)
 * @returns {{ yourDoctors: object[], orellaDoctors: object[] }}
 */
export function buildShareDoctorPanel({ available = [], favoriteIds = [], cases = [], matched = [] } = {}) {
  const favSet = new Set(favoriteIds || [])
  const seenSet = new Set((cases || []).map((c) => c && c.doctor_id).filter(Boolean))

  const yourDoctors = (available || [])
    .filter((d) => isBookable(d) && (favSet.has(d.id) || seenSet.has(d.id)))
    .map((d) => ({ ...withNextSlot(d), _isFavorite: favSet.has(d.id), _isSeen: seenSet.has(d.id) }))
    .sort(bySoonest)

  const yourIds = new Set(yourDoctors.map((d) => d.id))
  const orellaDoctors = (matched || [])
    .filter((d) => isBookable(d) && !yourIds.has(d.id))
    .map(withNextSlot)
    .sort(bySoonest)

  return { yourDoctors, orellaDoctors }
}

/**
 * loadShareDoctorPanel — P11 step 4 (D1): the panel's READ-ONLY load, moved here verbatim
 * from DiagnoseScreen's openDoctorPanel so ShareChoiceScreen's Orella card (TEST 2.19) runs
 * the SAME load and not a copy. The api objects and the matcher are passed in, so this file
 * stays import-free and check-share-link-doctor-panel.mjs can EXECUTE it with stub APIs whose
 * write methods log.
 *
 * @param {object} args
 * @param {object} args.casesAPI      needs list()
 * @param {object} args.doctorsAPI    needs list()          (GET /doctors/available)
 * @param {object} args.favoritesAPI  needs getDoctors()
 * @param {Function} args.matchDoctorsToCase      utils/doctorMatch
 * @param {Function} args.getMatchingSpecialties  utils/doctorMatch
 * @param {object} args.currentCase   reads specialtyId and focusAreaId only
 * @returns {Promise<{ yourDoctors: object[], orellaDoctors: object[] }>}
 */
export async function loadShareDoctorPanel({ casesAPI, doctorsAPI, favoritesAPI, matchDoctorsToCase, getMatchingSpecialties, currentCase } = {}) {
  const [casesResult, listResult, favs] = await Promise.all([
    casesAPI.list().catch(() => []),
    doctorsAPI.list().catch(() => []),
    favoritesAPI.getDoctors().catch(() => []),
  ])
  const available = Array.isArray(listResult) ? listResult : []
  const caseList = Array.isArray(casesResult) ? casesResult : (casesResult?.cases || [])
  const matched = matchDoctorsToCase(available, getMatchingSpecialties(currentCase?.specialtyId, currentCase?.focusAreaId))
  return buildShareDoctorPanel({ available, favoriteIds: favs || [], cases: caseList, matched })
}
